/**
 * Chatbot Context Provider
 * 
 * Manages chatbot state, knowledge base, chat history, and AI interactions.
 * Uses OpenAI API via Firebase Cloud Function for intelligent responses with RAG.
 * Falls back to local knowledge base search when AI is not available.
 * 
 * Features:
 * - AI-powered responses (GPT-4o-mini)
 * - Page context awareness
 * - Knowledge base search (RAG)
 * - Conversation history
 * - Feedback tracking
 * - Cost optimization (caching, rate limiting)
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../auth/AuthContext';
import { db } from '../../utils/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collectionPath } from '../../utils/firestoreCollections';
import { 
  collection, 
  addDoc, 
  setDoc,
  updateDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  serverTimestamp,
  onSnapshot,
  increment
} from 'firebase/firestore';
import { sanitizeString } from '../../utils/sanitization';
import { awardPoints } from '../../utils/gamificationUtils';

const ChatbotContext = createContext(null);

// Firebase Functions instance
const functions = getFunctions();

/**
 * Generate document ID for chat sessions
 * Format: chatsession_{userId}_{timestamp}
 */
function generateChatSessionId(userId) {
  const timestamp = new Date().toISOString().replace(/[^0-9T]/g, '').substring(0, 15);
  return `chatsession_${userId}_${timestamp}`;
}

/**
 * Generate document ID for chat feedback
 * Format: chatfeedback_{userId}_{helpful|unhelpful}_{timestamp}
 */
function generateChatFeedbackId(userId, isHelpful) {
  const timestamp = new Date().toISOString().replace(/[^0-9T]/g, '').substring(0, 15);
  const helpfulness = isHelpful ? 'helpful' : 'unhelpful';
  return `chatfeedback_${userId}_${helpfulness}_${timestamp}`;
}

// Rate limiting configuration
const RATE_LIMIT = {
  messagesPerMinute: 10,
  maxConversationLength: 50
};

// Knowledge categories for better search
const KNOWLEDGE_CATEGORIES = {
  NAVIGATION: 'navigation',
  CUSTOMERS: 'customers',
  OPPORTUNITIES: 'opportunities',
  ORDERS: 'orders',
  TASKS: 'tasks',
  REPORTS: 'reports',
  EMAIL_MARKETING: 'email_marketing',
  ADMIN: 'admin',
  GENERAL: 'general'
};

// Page to category mapping
const PAGE_CATEGORY_MAP = {
  '/dashboard': KNOWLEDGE_CATEGORIES.GENERAL,
  '/customers': KNOWLEDGE_CATEGORIES.CUSTOMERS,
  '/customer/': KNOWLEDGE_CATEGORIES.CUSTOMERS,
  '/opportunities': KNOWLEDGE_CATEGORIES.OPPORTUNITIES,
  '/opportunity/': KNOWLEDGE_CATEGORIES.OPPORTUNITIES,
  '/orders': KNOWLEDGE_CATEGORIES.ORDERS,
  '/order/': KNOWLEDGE_CATEGORIES.ORDERS,
  '/tasks': KNOWLEDGE_CATEGORIES.TASKS,
  '/task/': KNOWLEDGE_CATEGORIES.TASKS,
  '/reports': KNOWLEDGE_CATEGORIES.REPORTS,
  '/campaigns': KNOWLEDGE_CATEGORIES.EMAIL_MARKETING,
  '/segments': KNOWLEDGE_CATEGORIES.EMAIL_MARKETING,
  '/templates': KNOWLEDGE_CATEGORIES.EMAIL_MARKETING,
  '/admin': KNOWLEDGE_CATEGORIES.ADMIN,
  '/settings': KNOWLEDGE_CATEGORIES.ADMIN,
  '/support': KNOWLEDGE_CATEGORIES.GENERAL
};

export const ChatbotProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [currentPage, setCurrentPage] = useState('/');
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [feedbackScores, setFeedbackScores] = useState({}); // Track feedback-boosted entries
  const [userTopics, setUserTopics] = useState({}); // Track user's frequently asked topics
  const [recentCategory, setRecentCategory] = useState(null); // Most recently asked category
  const [error, setError] = useState(null);
  const messageCountRef = useRef({ count: 0, resetTime: Date.now() });

  // Generate personalized welcome message
  const getPersonalizedWelcome = useCallback(() => {
    const name = currentUser?.displayName?.split(' ')[0] || 'there';
    
    // Find user's most asked topics
    const sortedTopics = Object.entries(userTopics)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([topic]) => topic);
    
    // Standard welcome with personalization hints
    let personalizedHint = '';
    if (sortedTopics.length > 0) {
      // Map common words to friendly topic names
      const topicMappings = {
        'customer': 'customers',
        'customers': 'customers',
        'order': 'orders',
        'orders': 'orders',
        'report': 'reports',
        'reports': 'reports',
        'commission': 'commissions',
        'commissions': 'commissions',
        'opportunity': 'opportunities',
        'opportunities': 'opportunities',
        'task': 'tasks',
        'tasks': 'tasks',
        'import': 'imports',
        'export': 'exports',
        'email': 'email marketing',
        'search': 'search features'
      };
      
      const friendlyTopics = [...new Set(sortedTopics.map(t => topicMappings[t] || t))].slice(0, 2);
      if (friendlyTopics.length > 0) {
        personalizedHint = `\n\n💡 Based on your history, you often ask about **${friendlyTopics.join('** and **')}**. Need help with those?`;
      }
    }

    return `Hi ${name}! 👋 I'm your CRM assistant. I can help you with:

• **Navigation** - Find features and pages

• **How-to guides** - Step-by-step instructions

• **Tips & tricks** - Get more from the CRM

• **Troubleshooting** - Solve common issues${personalizedHint}

What can I help you with today?`;
  }, [currentUser?.displayName, userTopics]);

  // Initialize session
  useEffect(() => {
    if (currentUser && !sessionId) {
      const newSessionId = `chat_${currentUser.uid}_${Date.now()}`;
      setSessionId(newSessionId);
      
      // Add welcome message (will be personalized once userTopics loads)
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: getPersonalizedWelcome(),
        timestamp: new Date().toISOString()
      }]);
    }
  }, [currentUser, sessionId, getPersonalizedWelcome]);

  // Load knowledge base
  useEffect(() => {
    const loadKnowledgeBase = async () => {
      try {
        // Simple query without compound index requirements
        const snapshot = await getDocs(
          collection(db, collectionPath('chatbotKnowledge'))
        );
        
        // Filter and sort client-side to avoid index requirements
        const knowledge = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(entry => entry.isActive !== false) // Include if isActive is true or undefined
          .sort((a, b) => (b.priority || 50) - (a.priority || 50));
        
        setKnowledgeBase(knowledge);
        
        // Load aggregated feedback scores
        await loadFeedbackScores();
      } catch (err) {
        console.error('[Chatbot] Failed to load knowledge base:', err);
        setKnowledgeBase([]);
      }
    };
    
    // Load feedback scores to boost/penalize knowledge entries
    const loadFeedbackScores = async () => {
      try {
        const feedbackSnapshot = await getDocs(
          query(
            collection(db, collectionPath('chatFeedback')),
            orderBy('timestamp', 'desc'),
            limit(500) // Last 500 feedbacks
          )
        );
        
        // Aggregate feedback by knowledge entry keywords
        const scores = {};
        feedbackSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const question = (data.userQuestion || '').toLowerCase();
          const keywords = question.split(/\s+/).filter(w => w.length > 3);
          const scoreChange = data.isHelpful ? 1 : -1;
          
          keywords.forEach(keyword => {
            if (!scores[keyword]) {
              scores[keyword] = { helpful: 0, unhelpful: 0 };
            }
            if (data.isHelpful) {
              scores[keyword].helpful++;
            } else {
              scores[keyword].unhelpful++;
            }
          });
        });
        
        setFeedbackScores(scores);
      } catch (err) {
        // Silently fail - feedback scores are optional enhancement
      }
    };

    loadKnowledgeBase();
  }, []);

  // Load user's frequently asked topics
  useEffect(() => {
    const loadUserTopics = async () => {
      if (!currentUser?.uid) return;
      
      try {
        // Get this user's chat feedback history
        const userFeedbackSnapshot = await getDocs(
          query(
            collection(db, collectionPath('chatFeedback')),
            where('userId', '==', currentUser.uid),
            orderBy('timestamp', 'desc'),
            limit(100)
          )
        );
        
        // Count topics/categories from user's questions
        const topicCounts = {};
        userFeedbackSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const question = (data.userQuestion || '').toLowerCase();
          
          // Extract meaningful keywords as topics
          const words = question.split(/\s+/).filter(w => w.length > 3);
          words.forEach(word => {
            topicCounts[word] = (topicCounts[word] || 0) + 1;
          });
        });
        
        setUserTopics(topicCounts);
      } catch (err) {
        // May fail due to missing index, that's okay - user topics are optional
      }
    };

    loadUserTopics();
  }, [currentUser?.uid]);

  // Rate limiting check
  const checkRateLimit = useCallback(() => {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;
    
    if (messageCountRef.current.resetTime < oneMinuteAgo) {
      messageCountRef.current = { count: 0, resetTime: now };
    }
    
    if (messageCountRef.current.count >= RATE_LIMIT.messagesPerMinute) {
      return false;
    }
    
    messageCountRef.current.count++;
    return true;
  }, []);

  // Get category from current page
  const getCategoryFromPage = useCallback((page) => {
    for (const [pattern, category] of Object.entries(PAGE_CATEGORY_MAP)) {
      if (page.startsWith(pattern)) {
        return category;
      }
    }
    return KNOWLEDGE_CATEGORIES.GENERAL;
  }, []);

  // Search knowledge base
  const searchKnowledge = useCallback((query, category = null) => {
    const queryLower = query.toLowerCase();
    // More lenient: include 2+ character words for better matching
    const queryWords = queryLower.split(/\s+/).filter(w => w.length > 1);
    
    // Simple stemming - remove common suffixes for better matching
    const stemWord = (word) => {
      return word
        .replace(/ing$/, '')
        .replace(/ed$/, '')
        .replace(/s$/, '')
        .replace(/ly$/, '');
    };
    
    const stemmedQueryWords = queryWords.map(stemWord);
    
    // Calculate feedback boost for an entry based on query keywords
    const getFeedbackBoost = (entry) => {
      let boost = 0;
      const entryKeywords = [
        ...(entry.keywords || []),
        ...(entry.title || '').toLowerCase().split(/\s+/)
      ];
      
      queryWords.forEach(qw => {
        if (feedbackScores[qw]) {
          // Positive feedback = boost, negative = penalty
          const score = feedbackScores[qw];
          const netScore = score.helpful - score.unhelpful;
          
          // Check if this entry is relevant to the keyword
          if (entryKeywords.some(ek => ek.toLowerCase().includes(qw) || qw.includes(ek.toLowerCase()))) {
            boost += netScore * 0.5; // Weight the feedback influence
          }
        }
      });
      
      return boost;
    };
    
    return knowledgeBase
      .filter(entry => {
        // Category filter - GENERAL means search all, otherwise prioritize matching category
        // We don't filter out by category, we'll boost matching categories in sorting instead
        // This ensures questions asked from any page can find relevant answers
        
        // Search in title, content, and keywords
        const searchText = `${entry.title || ''} ${entry.content || ''} ${(entry.keywords || []).join(' ')}`.toLowerCase();
        
        // Check for any word match (original or stemmed)
        return queryWords.some(word => searchText.includes(word)) ||
               stemmedQueryWords.some(word => word.length > 2 && searchText.includes(word));
      })
      .map(entry => {
        // Word match score
        const wordMatchScore = queryWords.filter(w => 
          `${entry.title || ''} ${(entry.keywords || []).join(' ')}`.toLowerCase().includes(w)
        ).length;
        
        // FAQ source boost (+2 for official FAQs)
        const faqBoost = entry.source === 'faq' ? 2 : 0;
        
        // PAGE-CONTEXT BOOSTING: +2 if entry category matches current page category
        const pageContextBoost = (category && entry.category && 
          entry.category.toLowerCase() === category.toLowerCase()) ? 2 : 0;
        
        // Feedback boost from user ratings
        const feedbackBoost = getFeedbackBoost(entry);
        
        // Priority bonus (higher priority = small boost)
        const priorityBonus = (entry.priority || 0) / 10;
        
        return {
          ...entry,
          _score: wordMatchScore + faqBoost + pageContextBoost + feedbackBoost + priorityBonus
        };
      })
      .sort((a, b) => b._score - a._score)
      .slice(0, 5);
  }, [knowledgeBase, feedbackScores]);

  // Handle generic/vague queries that need clarification
  const handleGenericQuery = (messageLower, knowledge, category, page) => {
    // Define generic patterns and their structured responses
    const genericPatterns = [
      {
        // "show me reports", "reports", "what reports", "available reports"
        pattern: /^(show\s*(me\s*)?(the\s*)?)?reports?$|^what\s+reports?|^available\s+reports?|^view\s+reports?|^see\s+reports?|^go\s+to\s+reports?/i,
        category: 'reports',
        response: () => {
          return `## 📊 Reports

I can help you with reports! Click any link below to navigate directly:

**Available Reports:**
• [📊 Reports Overview](/reports) - Dashboard with key metrics
• [👥 Sales Rep Reports](/reports/reps) - Performance by sales rep
• [📈 Opportunity Reports](/reports/opportunities) - Pipeline analysis
• [🏢 Customer Reports](/reports/customers) - Customer analytics
• [🛒 Order Reports](/reports/orders) - Order history & trends
• [✅ Task Reports](/reports/tasks) - Task completion stats
• [⏱️ Inactivity Reports](/reports/inactivity) - Find dormant customers
• [🔀 Comparison Reports](/reports/comparison) - Compare time periods

**Need Help?**
Ask me questions like:
• _"How do I filter reports by date?"_
• _"How do I export a report?"_
• _"How do I subscribe to a report?"_

Which report are you looking for, or what would you like to learn?`;
        }
      },
      {
        // "show me customers", "customers", "view customers"
        pattern: /^(show\s*(me\s*)?(the\s*)?)?customers?$|^view\s+customers?|^see\s+customers?|^go\s+to\s+customers?|^manage\s+customers?/i,
        category: 'customers',
        response: () => {
          return `## 🏢 Customers

I can help you with customers! Here are your options:

**1. Quick Actions:**
• [View All Customers](/customers) - Browse and search customers
• [[How do I add a new customer?]]
• [[How do I import customers?]]

**2. Common Questions:**
• [[How do I find a specific customer?]]
• [[How do I edit customer information?]]
• [[How do I view a customer's order history?]]

**3. Related Pages:**
• [Customer Reports](/reports/customers) - Customer analytics
• [Inactivity Reports](/reports/inactivity) - Find dormant accounts

What would you like to do?`;
        }
      },
      {
        // "opportunities", "show me opportunities", "pipeline"
        pattern: /^(show\s*(me\s*)?(the\s*)?)?opportunities?$|^view\s+opportunities?|^pipeline$|^sales\s+pipeline$|^manage\s+opportunities?/i,
        category: 'opportunities',
        response: () => {
          return `## 📈 Opportunities

I can help you with opportunities! Here are your options:

**1. Quick Actions:**
• [View Pipeline](/opportunities) - See all opportunities
• [[How do I create an opportunity?]]
• [[How do I move an opportunity to a different stage?]]

**2. Common Questions:**
• [[What is Win Rate?]]
• [[How do I track opportunity value?]]
• [[How do I assign an opportunity to a sales rep?]]

**3. Related Pages:**
• [Opportunity Reports](/reports/opportunities) - Pipeline analytics
• [Dashboard](/dashboard) - See pipeline summary

What would you like to do?`;
        }
      },
      {
        // "tasks", "show me tasks", "my tasks"
        pattern: /^(show\s*(me\s*)?(the\s*)?)?tasks?$|^my\s+tasks?$|^view\s+tasks?|^manage\s+tasks?/i,
        category: 'tasks',
        response: () => {
          return `## ✅ Tasks

I can help you with tasks! Here are your options:

**1. Quick Actions:**
• [View All Tasks](/tasks) - See and manage tasks
• [[How do I create a new task?]]
• [[How do I mark a task as complete?]]

**2. Common Questions:**
• [[How do I filter tasks by status?]]
• [[How do I assign a task to someone?]]
• [[How do I link a task to a customer?]]

**3. Related Pages:**
• [Task Reports](/reports/tasks) - Task completion analytics
• [Dashboard](/dashboard) - See upcoming tasks

What would you like to do?`;
        }
      },
      {
        // "emails", "email marketing", "campaigns"
        pattern: /^(show\s*(me\s*)?(the\s*)?)?emails?$|^email\s+(marketing|campaigns?)$|^campaigns?$|^marketing$/i,
        category: 'email',
        response: () => {
          return `## 📧 Email Marketing

I can help you with email marketing! Here are your options:

**1. Quick Actions:**
• [Email Builder](/email-builder) - Create email campaigns
• [Email Templates](/email-templates) - Manage templates
• [[How do I create an email campaign?]]

**2. Common Questions:**
• [[How do I send a test email?]]
• [[How do I schedule a campaign?]]
• [[How do I build a recipient list?]]

**3. Related Features:**
• Customer segmentation for targeting
• Template library for quick creation
• Campaign analytics and tracking

What would you like to do?`;
        }
      },

      {
        // "help", "what can you do", "menu", "options"
        pattern: /^help$|^what\s+can\s+you\s+do|^menu$|^options$|^main\s+menu$/i,
        category: 'help',
        response: () => {
          return `## 🤖 CRM Assistant

I can help you navigate and use the CRM! Here are the main areas:

**📊 Reports**
• [[Show me reports]] - View analytics and metrics

**🏢 Customers**
• [[Show me customers]] - Manage customer accounts

**📈 Opportunities**
• [[Show me opportunities]] - Track sales pipeline

**✅ Tasks**
• [[Show me tasks]] - Manage follow-ups

**📧 Email Marketing**
• [[Show me email marketing]] - Create campaigns

**🔍 Other Actions:**
• Type **"help with this page"** for page-specific help
• Visit the [Support Page](/support) for full documentation

What area would you like help with?`;
        }
      },
      {
        // "dashboard", "home", "show me dashboard"
        pattern: /^(show\s*(me\s*)?(the\s*)?)?dashboard$|^home$/i,
        category: 'dashboard',
        response: () => {
          return `## 🏠 Dashboard

The Dashboard is your CRM home base! Here's what you can do:

**Quick Actions:**
• [Go to Dashboard](/dashboard)

**What's on the Dashboard:**
• **Pipeline Summary** - Your opportunities by stage
• **Recent Activity** - Latest updates and changes
• **Open Tasks** - Upcoming follow-ups
• **Key Metrics** - Performance at a glance

**Common Questions:**
• [[What is the Dashboard and what does it show?]]
• [[How do I customize my dashboard?]]

Would you like me to explain any specific dashboard feature?`;
        }
      }
    ];

    // Check each pattern
    for (const { pattern, response } of genericPatterns) {
      if (pattern.test(messageLower)) {
        const result = response();
        if (result) return result;
      }
    }

    return null; // Not a generic query, continue with normal processing
  };

  // State for AI availability
  const [aiEnabled, setAiEnabled] = useState(true); // Assume AI is enabled initially
  const aiFailureCountRef = useRef(0);

  // Generate contextual response - tries AI first, falls back to local
  // Returns { content: string, matchedArticleId: string|null }
  const generateResponse = useCallback(async (userMessage) => {
    const category = getCategoryFromPage(currentPage);
    const relevantKnowledge = searchKnowledge(userMessage, category);

    // Try AI-powered response first (if enabled and not too many failures)
    if (aiEnabled && aiFailureCountRef.current < 3) {
      try {
        const chatWithAI = httpsCallable(functions, 'chatWithAI');
        
        // Prepare conversation history (last 6 messages for context)
        const conversationHistory = messages
          .filter(m => m.role === 'user' || m.role === 'assistant')
          .slice(-6)
          .map(m => ({ role: m.role, content: m.content }));
        
        // Prepare knowledge context (top 5 relevant articles)
        const knowledgeContext = relevantKnowledge.slice(0, 5).map(k => ({
          title: k.title,
          content: k.content?.substring(0, 500) || '', // Truncate for token efficiency
          category: k.category
        }));
        
        const result = await chatWithAI({
          message: userMessage,
          conversationHistory,
          knowledgeContext,
          currentPage
        });
        
        if (result.data.success) {
          aiFailureCountRef.current = 0; // Reset failure count on success
          return { 
            content: result.data.response, 
            matchedArticleId: null,
            aiPowered: true 
          };
        }
        
        // AI returned fallback flag - use local processing
        if (result.data.fallback) {
          console.log('[Chatbot] AI not configured, using local processing');
          setAiEnabled(false); // Disable AI for this session
        }
      } catch (err) {
        console.warn('[Chatbot] AI request failed, falling back to local:', err.message);
        aiFailureCountRef.current++;
        
        // After 3 failures, disable AI for this session
        if (aiFailureCountRef.current >= 3) {
          console.log('[Chatbot] Disabling AI after multiple failures');
          setAiEnabled(false);
        }
      }
    }

    // Fall back to local rule-based responses + knowledge base
    const result = await generateLocalResponse(userMessage, relevantKnowledge, category, currentPage);
    
    return result;
  }, [currentPage, getCategoryFromPage, searchKnowledge, messages, aiEnabled]);

  // Local response generation (no API cost) - fallback when AI is unavailable
  // Returns { content: string, matchedArticleId: string|null }
  const generateLocalResponse = async (userMessage, knowledge, category, page) => {
    const messageLower = userMessage.toLowerCase().trim();
    
    // Check for greetings
    if (/^(hi|hello|hey|good morning|good afternoon|good evening)[\s!.]*$/i.test(messageLower)) {
      return { content: `Hello! How can I help you today? I'm here to assist with anything related to the CRM.`, matchedArticleId: null };
    }
    
    // Check for thanks
    if (/^(thanks|thank you|thx|ty|great|perfect|awesome)[\s!.]*$/i.test(messageLower)) {
      return { content: `You're welcome! Is there anything else I can help you with?`, matchedArticleId: null };
    }

    // Handle generic/vague category requests that need clarification
    const genericQueryResponse = handleGenericQuery(messageLower, knowledge, category, page);
    if (genericQueryResponse) {
      return { content: genericQueryResponse, matchedArticleId: null };
    }
    
    // Handle "Help with this page" - page-specific contextual help
    if (/help.*this page|this page.*help|what.*this page|about this page/i.test(messageLower)) {
      const pageHelp = getPageSpecificHelp(page);
      if (pageHelp) {
        let response = `## ${pageHelp.name} Page\n\n${pageHelp.help}`;
        
        // Add page-specific tips
        if (pageHelp.tips && pageHelp.tips.length > 0) {
          response += '\n\n**Quick Tips:**\n' + pageHelp.tips.map((t, i) => `${i + 1}. ${t}`).join('\n');
        }
        
        // Suggest related FAQs for this page category
        const relatedFaqs = knowledge
          .filter(k => k.source === 'faq' && k.category === category)
          .slice(0, 3);
        
        if (relatedFaqs.length > 0) {
          response += '\n\n**Common questions for this page:**\n' + 
            relatedFaqs.map(f => `• ${f.title}`).join('\n');
        }
        
        return { content: response, matchedArticleId: null };
      }
    }

    // Check if there's relevant knowledge
    if (knowledge.length > 0) {
      const topMatch = knowledge[0];
      const queryWords = messageLower.split(/\s+/).filter(w => w.length > 1);
      
      // Calculate relevance score for top match
      const titleLower = (topMatch.title || '').toLowerCase();
      const contentLower = (topMatch.content || '').toLowerCase();
      const keywordsStr = (topMatch.keywords || []).join(' ').toLowerCase();
      const stepsStr = (topMatch.steps || []).join(' ').toLowerCase();
      const allText = `${titleLower} ${contentLower} ${keywordsStr} ${stepsStr}`;
      
      // Check for exact or near-exact question match
      const questionMatch = knowledge.find(k => {
        const kTitle = (k.title || '').toLowerCase();
        // Check if user's question is very similar to a FAQ title
        const similarity = calculateSimilarity(messageLower, kTitle);
        return similarity > 0.6;
      });
      
      if (questionMatch) {
        return { content: formatKnowledgeResponse(questionMatch, knowledge), matchedArticleId: questionMatch.id };
      }
      
      // Calculate word match score
      const matchedWords = queryWords.filter(w => allText.includes(w));
      const relevanceScore = matchedWords.length / Math.max(queryWords.length, 1);
      
      // High relevance - provide direct answer with steps
      if (relevanceScore >= 0.35 || matchedWords.length >= 2) {
        return { content: formatKnowledgeResponse(topMatch, knowledge), matchedArticleId: topMatch.id };
      }
      
      // Medium relevance - suggest relevant topics with more detail (no single article match)
      let response = `I found some topics that might help:\n\n`;
      knowledge.slice(0, 3).forEach((k, i) => {
        response += `**${i + 1}. ${k.title}**\n`;
        response += `${k.content}\n`;
        if (k.steps && k.steps.length > 0) {
          response += `_(${k.steps.length} steps available)_\n`;
        }
        response += '\n';
      });
      response += `\nAsk about any of these topics for detailed step-by-step instructions!`;
      return { content: response, matchedArticleId: null };
    }
    
    // Page-specific help as fallback
    const pageHelp = getPageSpecificHelp(page);
    if (pageHelp) {
      let response = `I'm not sure about that specific question, but here's what you can do on the **${pageHelp.name}** page:\n\n${pageHelp.help}`;
      if (pageHelp.tips && pageHelp.tips.length > 0) {
        response += '\n\n**Tips:**\n' + pageHelp.tips.map((t, i) => `${i + 1}. ${t}`).join('\n');
      }
      response += '\n\nCan you rephrase your question, or ask about a specific task?';
      return { content: response, matchedArticleId: null };
    }
    
    // Default response with suggestions
    return { content: `I'm not sure I understand your question. Try asking something like:\n
• **"How do I create a customer?"**
• **"How do I move an opportunity to a different stage?"**
• **"What reports are available?"**
• **"How do I create an email campaign?"**

You can also type **"help with this page"** to learn about your current page, or visit the [Support Page](/support) for all documentation.`, matchedArticleId: null };
  };
  
  // Calculate text similarity (Jaccard-like)
  const calculateSimilarity = (text1, text2) => {
    const words1 = new Set(text1.toLowerCase().split(/\s+/).filter(w => w.length > 2));
    const words2 = new Set(text2.toLowerCase().split(/\s+/).filter(w => w.length > 2));
    
    const intersection = [...words1].filter(w => words2.has(w)).length;
    const union = new Set([...words1, ...words2]).size;
    
    return union > 0 ? intersection / union : 0;
  };
  
  // Format a knowledge entry as a complete response
  const formatKnowledgeResponse = (entry, allKnowledge) => {
    let response = '';
    
    // Title as header
    if (entry.title) {
      response += `## ${entry.title}\n\n`;
    }
    
    // Main content
    response += entry.content;
    
    // Steps - the key improvement!
    if (entry.steps && entry.steps.length > 0) {
      response += '\n\n**Step-by-Step:**\n';
      entry.steps.forEach((step, i) => {
        response += `${i + 1}. ${step}\n`;
      });
    }
    
    // Related topics - using [[...]] syntax for clickable questions
    const related = allKnowledge
      .filter(k => k.id !== entry.id)
      .slice(0, 2);
    
    if (related.length > 0) {
      response += '\n\n**Related:** ' + related.map(k => `[[${k.title}]]`).join(' • ');
    }
    
    return response;
  };

  // Page-specific help with detailed tips
  const getPageSpecificHelp = (page) => {
    const pageHelp = {
      '/dashboard': {
        name: 'Dashboard',
        help: 'Here you can see your key metrics, recent activities, open tasks, and quick access to important features. The dashboard shows your pipeline summary, recent orders, and upcoming follow-ups.',
        tips: [
          'Click on any metric card to drill down into details',
          'Use the date range selector to view different time periods',
          'Your open tasks are sorted by priority - tackle red ones first!',
          'Click "View All" links to see complete lists'
        ]
      },
      '/customers': {
        name: 'Customers',
        help: 'Manage your wholesale customers here. You can add new customers, edit details, view order history, and track relationships. Use the search bar to quickly find customers by name, account number, or sales rep.',
        tips: [
          'Click the + button to add a new customer',
          'Use filters to narrow down by status, sales rep, or territory',
          'Click a row to view full customer details and order history',
          'Export customers to CSV using the export button'
        ]
      },
      '/customers/': {
        name: 'Customer Details',
        help: 'View and edit all information for this customer including contacts, orders, opportunities, and notes. You can also view their complete order history synced from ApparelMagic.',
        tips: [
          'Edit customer info by clicking the Edit button',
          'Add contacts in the Contacts tab',
          'View all orders in the Orders tab',
          'Create opportunities directly from this page'
        ]
      },
      '/opportunities': {
        name: 'Opportunities',
        help: 'Track your sales pipeline here. Create opportunities for potential deals, update stages as they progress, and monitor your sales forecast. The Kanban view shows all opportunities by stage.',
        tips: [
          'Drag and drop cards to change stages in Kanban view',
          'Click + to create a new opportunity',
          'Use filters to focus on specific stages or reps',
          'Click a card to see full opportunity details'
        ]
      },
      '/tasks': {
        name: 'Tasks',
        help: 'Manage your follow-up tasks and to-do items. Tasks can be linked to customers and opportunities. Overdue tasks are highlighted in red, due today in yellow.',
        tips: [
          'Click + to create a new task',
          'Check the box to mark a task complete',
          'Filter by status: All, Open, Completed, Overdue',
          'Link tasks to customers or opportunities for context'
        ]
      },
      '/orders': {
        name: 'Orders',
        help: 'View orders synced from ApparelMagic. Track order status, line items, shipping, and commissions. Search by order number, customer, or PO number.',
        tips: [
          'Use the search bar to find orders quickly',
          'Filter by date range to see specific periods',
          'Click a row to view full order details',
          'Export orders to CSV for reporting'
        ]
      },
      '/reports': {
        name: 'Reports',
        help: 'Analyze your business performance with various reports. View revenue trends, pipeline analysis, rep performance, and more. Reports can be customized by date range and filters.',
        tips: [
          'Select different date ranges to compare periods',
          'Use the report selector to switch between report types',
          'Export any report to PDF or Excel',
          'Subscribe to reports for automatic email delivery'
        ]
      },
      '/email': {
        name: 'Email Marketing',
        help: 'Create and send email campaigns to your customers. Build contact lists, design emails with the template editor, and track campaign performance.',
        tips: [
          'Start by creating a contact list based on customer criteria',
          'Use the template editor to design professional emails',
          'Always send a test email before the full campaign',
          'Track opens and clicks in the campaign analytics'
        ]
      },
      '/notes': {
        name: 'Notes',
        help: 'View and manage notes across all customers and opportunities. Notes provide a history of interactions and important information.',
        tips: [
          'Filter notes by customer or opportunity',
          'Use the search to find specific notes',
          'Notes are automatically timestamped with the author'
        ]
      },
      '/admin': {
        name: 'Admin',
        help: 'System administration area. Manage users, roles, permissions, and system settings. Only administrators have access to this section.',
        tips: [
          'Add new users in the Users tab',
          'Assign roles to control permissions',
          'Configure system-wide settings',
          'View audit logs for security tracking'
        ]
      },
      '/support': {
        name: 'Support',
        help: 'Find help and documentation for all CRM features. Browse FAQs by category, search for specific topics, or use the chatbot for quick assistance.',
        tips: [
          'Use the search bar to find specific help topics',
          'Browse categories in the sidebar',
          'Click any FAQ to expand and see detailed steps',
          'Use the chatbot for interactive help'
        ]
      }
    };
    
    // Find the best matching page
    for (const [path, help] of Object.entries(pageHelp)) {
      if (page === path || (path.endsWith('/') && page.startsWith(path))) {
        return help;
      }
    }
    // Fallback to prefix match
    for (const [path, help] of Object.entries(pageHelp)) {
      if (page.startsWith(path)) {
        return help;
      }
    }
    return null;
  };

  // Send message
  const sendMessage = useCallback(async (content) => {
    if (!content.trim() || !currentUser) return;
    
    // Rate limit check
    if (!checkRateLimit()) {
      setError('You\'re sending messages too quickly. Please wait a moment.');
      return;
    }
    
    // Conversation length check
    if (messages.length >= RATE_LIMIT.maxConversationLength) {
      setError('Conversation is too long. Please start a new chat.');
      return;
    }

    setError(null);
    const sanitizedContent = sanitizeString(content, 1000);
    
    // Add user message
    const userMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: sanitizedContent,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Generate response (returns { content, matchedArticleId, aiPowered? })
      const responseResult = await generateResponse(sanitizedContent);
      
      // Add assistant message with matched article ID for feedback tracking
      const assistantMessage = {
        id: `assistant_${Date.now()}`,
        role: 'assistant',
        content: responseResult.content,
        matchedArticleId: responseResult.matchedArticleId,
        aiPowered: responseResult.aiPowered || false, // Track if AI generated this response
        timestamp: new Date().toISOString(),
        feedbackGiven: false
      };
      
      setMessages(prev => [...prev, assistantMessage]);

      // Log to Firestore (async, don't wait)
      logChatMessage(userMessage, assistantMessage).catch(console.error);
      
    } catch (err) {
      console.error('[Chatbot] Error generating response:', err);
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        role: 'assistant',
        content: 'I encountered an error processing your request. Please try again or visit the [Support Page](/support) for help.',
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, messages.length, checkRateLimit, generateResponse]);

  // Log chat message to Firestore
  const logChatMessage = async (userMessage, assistantMessage) => {
    if (!currentUser || !sessionId) return;

    try {
      const category = getCategoryFromPage(currentPage);
      
      // Update recent category for personalization
      setRecentCategory(category);
      
      // Update local userTopics count
      const words = (userMessage.content || '').toLowerCase().split(/\s+/).filter(w => w.length > 3);
      setUserTopics(prev => {
        const updated = { ...prev };
        words.forEach(word => {
          updated[word] = (updated[word] || 0) + 1;
        });
        return updated;
      });

      // Use proper document ID following naming conventions
      const docId = generateChatSessionId(currentUser.uid);
      await setDoc(doc(db, collectionPath('chatSessions'), docId), {
        sessionId,
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        userRole: currentUser.role || 'user',
        page: currentPage,
        category, // Track category for smarter recommendations
        userMessage: userMessage.content,
        assistantMessage: assistantMessage.content,
        timestamp: serverTimestamp(),
        createdAt: serverTimestamp()
      });

      // Increment lifetime session counter
      try {
        await setDoc(doc(db, collectionPath('config'), 'chatbotStats'), {
          lifetimeSessions: increment(1),
          updatedAt: serverTimestamp()
        }, { merge: true });
      } catch (statsErr) {
        console.warn('[Chatbot] Failed to update lifetime stats:', statsErr);
      }
    } catch (err) {
      console.warn('[Chatbot] Failed to log chat:', err);
    }
  };

  // Submit feedback
  const submitFeedback = useCallback(async (messageId, isHelpful, comment = '') => {
    if (!currentUser) return;

    try {
      // Find the message
      const message = messages.find(m => m.id === messageId);
      if (!message || message.role !== 'assistant') return;

      // Find preceding user message
      const messageIndex = messages.findIndex(m => m.id === messageId);
      const userMessage = messageIndex > 0 ? messages[messageIndex - 1] : null;

      // Use proper document ID following naming conventions
      const docId = generateChatFeedbackId(currentUser.uid, isHelpful);
      await setDoc(doc(db, collectionPath('chatFeedback'), docId), {
        sessionId,
        messageId,
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        userRole: currentUser.role || 'user',
        page: currentPage,
        userQuestion: userMessage?.content || '',
        assistantResponse: message.content,
        matchedArticleId: message.matchedArticleId || null, // Track which KB article was used
        aiPowered: message.aiPowered || false, // Track if response was AI-generated
        isHelpful,
        comment: sanitizeString(comment, 500),
        timestamp: serverTimestamp(),
        reviewed: false,
        promotedToKB: false // Track if response was promoted to Knowledge Base
      });

      // Increment lifetime feedback counter
      try {
        await setDoc(doc(db, collectionPath('config'), 'chatbotStats'), {
          lifetimeFeedback: increment(1),
          updatedAt: serverTimestamp()
        }, { merge: true });
      } catch (statsErr) {
        console.warn('[Chatbot] Failed to update lifetime stats:', statsErr);
      }

      // Update local message state
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, feedbackGiven: true, feedback: isHelpful } : m
      ));

      // Gamification: Award points for feedback
      try {
        const action = isHelpful ? 'FEEDBACK_HELPFUL_SUBMITTED' : 'FEEDBACK_SUBMITTED';
        await awardPoints(currentUser.uid, action);
      } catch (gamErr) {
        console.warn('[Chatbot] Gamification tracking failed:', gamErr);
      }

      return true;
    } catch (err) {
      console.error('[Chatbot] Failed to submit feedback:', err);
      return false;
    }
  }, [currentUser, sessionId, messages, currentPage]);

  // Start new conversation
  const startNewConversation = useCallback(() => {
    const newSessionId = `chat_${currentUser?.uid || 'anon'}_${Date.now()}`;
    setSessionId(newSessionId);
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: `Starting fresh! 🔄 How can I help you?`,
      timestamp: new Date().toISOString()
    }]);
    setError(null);
  }, [currentUser]);

  // Toggle chatbot (used by floating button)
  const toggleChatbot = useCallback(() => {
    setIsOpen(prev => !prev);
    setIsMinimized(false);
    setIsDismissed(false);
  }, []);

  // Minimize chatbot (shows floating button)
  const minimizeChatbot = useCallback(() => {
    setIsMinimized(true);
    setIsOpen(false);
  }, []);

  // Dismiss chatbot completely (hides floating button too)
  const dismissChatbot = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(true);
    setIsDismissed(true);
  }, []);

  // Open chatbot (from header icon when dismissed)
  const openChatbot = useCallback(() => {
    setIsDismissed(false);
    setIsMinimized(false);
    setIsOpen(true);
  }, []);

  // Update current page
  const updateCurrentPage = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Reload knowledge base (called when admin disables/enables entries)
  const reloadKnowledgeBase = useCallback(async () => {
    try {
      const snapshot = await getDocs(
        collection(db, collectionPath('chatbotKnowledge'))
      );
      
      const knowledge = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(entry => entry.isActive !== false)
        .sort((a, b) => (b.priority || 50) - (a.priority || 50));
      
      setKnowledgeBase(knowledge);
      console.log(`[Chatbot] Reloaded ${knowledge.length} knowledge entries`);
    } catch (err) {
      console.error('[Chatbot] Failed to reload knowledge base:', err);
    }
  }, []);

  const value = {
    isOpen,
    isMinimized,
    isDismissed,
    messages,
    isLoading,
    error,
    currentPage,
    sessionId,
    sendMessage,
    submitFeedback,
    startNewConversation,
    toggleChatbot,
    minimizeChatbot,
    dismissChatbot,
    openChatbot,
    updateCurrentPage,
    reloadKnowledgeBase,
    KNOWLEDGE_CATEGORIES
  };

  return (
    <ChatbotContext.Provider value={value}>
      {children}
    </ChatbotContext.Provider>
  );
};

export const useChatbot = () => {
  const context = useContext(ChatbotContext);
  if (!context) {
    throw new Error('useChatbot must be used within a ChatbotProvider');
  }
  return context;
};

export default ChatbotContext;
